import { Map, Marker, useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import { Building, Building2, Loader2, MapPin, Pin, Search } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Input } from '../ui/input';
import { debounce } from 'lodash';
import mapPin from '@/assets/icons/map-pin.png';
import { toast } from 'sonner';
import { usePlans } from '@/contexts/PlanRulesContext';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@/services/queryKeyFactory';
import { logsServices } from '@/services/logs.service';


function GoogleMap({ searchMode, setSearchMode, clientHotel, competitorsHotel }) {

    const { yourHotel, setYourHotel } = clientHotel;
    const { competitors, setCompetitors } = competitorsHotel;

    const [searchQuery, setSearchQuery] = useState("");
    const map = useMap();
    const [placesService, setPlacesService] = useState(null)
    const placesLibrary = useMapsLibrary("places")
    const inputRef = useRef();
    const [autoCompleteService, setAutoCompleteService] = useState(null)
    const [predictions, setPredictions] = useState([]);

    const sessionTokenRef = useRef();

    const { maxCompetitorsPerCompset } = usePlans();


    const { data: ipInfo } = useQuery({
        queryKey: queryKeys.ipInfo(),
        queryFn: logsServices.getIpInfo,
    })


    useEffect(() => {
        if (!map) return;

        const bounds = new google.maps.LatLngBounds();

        if (yourHotel?.geometry) {
            bounds.extend(yourHotel.geometry);
        }

        competitors?.forEach((hotel) => {
            if (hotel?.geometry) {
                bounds.extend(hotel.geometry);
            }
        });

        const totalHotels =
            (yourHotel?.geometry ? 1 : 0) + competitors.length;

        if (totalHotels === 1) {
            map.setCenter(yourHotel?.geometry || competitors[0]?.geometry);
            map.setZoom(14);
        } else if (totalHotels > 1) {
            map.fitBounds(bounds, 80);
        }

    }, [yourHotel, competitors, map]);

    function onSelectPlace(place) {

        const hotel_detail = {
            hotelName: place?.name,
            geometry: {
                lat: place?.geometry?.location?.lat(),
                lng: place?.geometry?.location?.lng(),
            },
            placeId: place?.place_id,
            hotelAddress: place?.formatted_address,
            addressComponents: place?.address_components,
            removable: true
        }

        if (searchMode === "your_hotel") {

            const isHotelExistInCompetitors = competitors?.find(hotel => hotel?.placeId == place?.place_id)

            if (isHotelExistInCompetitors) {
                const filteredCompetitors = competitors?.filter(hotel => hotel?.placeId !== place?.place_id)
                setCompetitors(filteredCompetitors)
            }

            setYourHotel(hotel_detail)
            setSearchMode('competitor')
        } else {

            if (place?.place_id === yourHotel?.placeId) {
                toast.warning("A Comptitor hotel cannot be a client hotel");
                return
            }

            if (competitors.length >= maxCompetitorsPerCompset) {
                toast.warning("Max competitors reached!");
                return;
            }

            const isExist = competitors.find(comp => comp?.placeId === place?.place_id);

            if (isExist) return
            setCompetitors(prev => [...prev, hotel_detail])
        }
    }

    useEffect(() => {

        if (!placesLibrary || !map) return

        const service = new placesLibrary.AutocompleteService();
        setAutoCompleteService(service)

        const placeService = new placesLibrary.PlacesService(map)
        setPlacesService(placeService)

    }, [placesLibrary, map])



    const handleInputChange = (e) => {
        const value = e.target.value;
        setSearchQuery(value);

        if (!autoCompleteService || !value) {
            setPredictions([]);
            return;
        }

        if (!sessionTokenRef.current && placesLibrary) {
            const token = new placesLibrary.AutocompleteSessionToken();
            sessionTokenRef.current = token;
        }

        debouncedFetchSuggestions(value)

    };

    const debouncedFetchSuggestions = useMemo(() => {

        if (!autoCompleteService) return null;

        return debounce((value) => {
            autoCompleteService.getPlacePredictions(
                {
                    input: value,
                    types: ["lodging"],
                    sessionToken: sessionTokenRef.current

                },
                (results, status) => {
                    if (status === google.maps.places.PlacesServiceStatus.OK) {
                        setPredictions(results);
                    } else {
                        setPredictions([]);
                    }
                }
            );
        }, 400)

    }, [autoCompleteService])


    function handlePlaceSelection(placeId) {


        if (!placesService || !placeId) return

        placesService.getDetails(
            {
                placeId,
                fields: ['geometry', 'name', 'formatted_address', 'place_id', 'address_components', 'types',],
                sessionToken: sessionTokenRef.current
            },
            (place, status) => {
                if (status === google.maps.places.PlacesServiceStatus.OK) {
                    setPredictions([]);
                    setSearchQuery("");
                    onSelectPlace(place)
                    sessionTokenRef.current = new placesLibrary.AutocompleteSessionToken()
                }
            }
        )
    }


    return (
        <div>
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                    placeholder={searchMode === 'your_hotel' ? "Search for your hotel..." : "Search for competitor hotels..."}
                    value={searchQuery}
                    onChange={(e) => handleInputChange(e)}
                    className="pl-10 mb-4"
                    disabled={yourHotel?.placeId && competitors.length === maxCompetitorsPerCompset}
                    data-testid="hotel-search-input"
                />
                {predictions.length > 0
                    ? (
                        <ul style={{
                            position: 'absolute',
                            top: '105%',
                            left: 0,
                            right: 0,
                            backgroundColor: 'white',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            // maxHeight: '200px',
                            overflowY: 'auto',
                            zIndex: 1001,
                            margin: 0,
                            padding: 0,
                            listStyle: 'none'
                        }}>
                            {predictions.map((prediction) => (
                                <>
                                    <li
                                        key={prediction.place_id}
                                        onClick={() => handlePlaceSelection(prediction.place_id)}
                                        style={{
                                            padding: '8px 12px',
                                            cursor: 'pointer',
                                            borderBottom: '1px solid #eee',
                                            fontSize: '14px'
                                        }}
                                        className='flex items-center gap-2'
                                    // onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                                    // onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                                    >
                                        {/* <MapPin color='blue' className='w-14'/> */}
                                        <img src={mapPin} className='h-4 w-4' />
                                        <p> {prediction.description} </p>
                                    </li>
                                </>
                            ))}
                        </ul>
                    )
                    : searchQuery.length
                        ? <ul style={{
                            position: 'absolute',
                            top: '105%',
                            left: 0,
                            right: 0,
                            backgroundColor: 'white',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            maxHeight: '200px',
                            minHeight: '40px',
                            display: 'flex',
                            overflowY: 'auto',
                            zIndex: 1001,
                            margin: 0,
                            padding: 0,
                            listStyle: 'none'
                        }}>
                            <span className="py-4 px-4"> No results found </span>
                        </ul>
                        : null
                }
            </div>
            {/* <button onClick={fechSuggetions}> Test </button> */}
            <div style={{ height: "300px", width: "100%" }}>
                <Map
                    zoom={10}
                    defaultCenter={{ lat: ipInfo?.latitude || 53.54992, lng: ipInfo?.longitude || 10.00678 }}
                    style={{
                        height: "100%",
                        width: "100%"
                    }}
                    disableDefaultUI
                    backgroundColor='black'
                >
                    {yourHotel?.geometry && (
                        <Marker
                            position={{
                                lat: Number(yourHotel?.geometry?.lat),
                                lng: Number(yourHotel?.geometry?.lng)
                            }}
                            title={yourHotel?.hotelName}
                            icon={{
                                url: "https://maps.google.com/mapfiles/ms/icons/blue-dot.png"
                            }}
                        />
                    )}

                    {competitors?.map((hotel) => (
                        <Marker
                            key={hotel?.placeId}
                            position={{
                                lat: Number(hotel?.geometry?.lat),
                                lng: Number(hotel?.geometry?.lng)
                            }}
                            title={hotel?.hotelName}
                            icon={{
                                url: "https://maps.google.com/mapfiles/ms/icons/red-dot.png"
                            }}
                        />
                    ))}
                </Map>
            </div>
        </div>
    );
}

export default GoogleMap;